/**
 * Products Catalog Page
 * Gestion CRUD du catalogue produits
 */
import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { Plus, Edit2, Power, Search, Package, X, Save } from 'lucide-react';
import '../../../styles/admin/production/ProductsCatalog.css';

const ProductsCatalog = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: '',
    unit: '',
    default_application_unit: '',
    cost_per_unit: 0,
    selling_price_per_unit: 0,
    supplier: '',
    notes: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, searchTerm, categoryFilter]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/production/products');
      setProducts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement produits:', error);
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = products;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(term) ||
        (p.sku && p.sku.toLowerCase().includes(term)) ||
        (p.category && p.category.toLowerCase().includes(term))
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter(p => p.category === categoryFilter);
    }

    setFilteredProducts(filtered);
  };

  const handleCreate = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      sku: '',
      category: '',
      unit: '',
      default_application_unit: '',
      cost_per_unit: 0,
      selling_price_per_unit: 0,
      supplier: '',
      notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      sku: product.sku || '',
      category: product.category || '',
      unit: product.unit,
      default_application_unit: product.default_application_unit || '',
      cost_per_unit: product.cost_per_unit,
      selling_price_per_unit: product.selling_price_per_unit || 0,
      supplier: product.supplier || '',
      notes: product.notes || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingProduct) {
        await api.put(`/api/production/products/${editingProduct.id}`, formData);
      } else {
        await api.post('/api/production/products', formData);
      }
      
      await fetchProducts();
      setShowModal(false);
    } catch (error) {
      console.error('Erreur sauvegarde produit:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleToggleActive = async (productId, currentStatus) => {
    const action = currentStatus ? 'désactiver' : 'activer';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ce produit ?`)) return;
    
    try {
      await api.put(`/api/production/products/${productId}`, { is_active: !currentStatus });
      await fetchProducts();
    } catch (error) {
      console.error('Erreur changement statut:', error);
      alert('Erreur lors du changement de statut');
    }
  };

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];

  if (loading) {
    return <div className="loading-container">Chargement...</div>;
  }

  return (
    <div className="products-catalog-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Catalogue Produits</h1>
          <p className="subtitle">Gestion des produits et matériaux utilisés sur le terrain</p>
        </div>
        <button className="btn-primary" onClick={handleCreate}>
          <Plus size={20} />
          Nouveau Produit
        </button>
      </div>

      {/* Filtres */}
      <div className="filters-section">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Rechercher par nom, SKU ou catégorie..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <label>Catégorie</label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">Toutes les catégories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-bar">
        <div className="stat">
          <span className="stat-label">Total Produits:</span>
          <span className="stat-value">{products.length}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Actifs:</span>
          <span className="stat-value">{products.filter(p => p.is_active).length}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Catégories:</span>
          <span className="stat-value">{categories.length}</span>
        </div>
      </div>

      {/* Tableau Produits */}
      <div className="products-table-container">
        <table className="products-table">
          <thead>
            <tr>
              <th>Statut</th>
              <th>Nom</th>
              <th>SKU</th>
              <th>Catégorie</th>
              <th>Unité</th>
              <th>Coût Unitaire</th>
              <th>Prix Vente</th>
              <th>Fournisseur</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">
                  {searchTerm || categoryFilter 
                    ? 'Aucun produit ne correspond aux filtres' 
                    : 'Aucun produit. Créez votre premier produit.'}
                </td>
              </tr>
            ) : (
              filteredProducts.map((product) => (
                <tr key={product.id} className={!product.is_active ? 'inactive-row' : ''}>
                  <td>
                    <span className={`status-indicator ${product.is_active ? 'active' : 'inactive'}`}>
                      {product.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="product-name">{product.name}</td>
                  <td className="sku">{product.sku || '-'}</td>
                  <td>
                    {product.category && (
                      <span className="category-badge">{product.category}</span>
                    )}
                  </td>
                  <td>{product.unit}</td>
                  <td className="currency">${product.cost_per_unit.toFixed(2)}</td>
                  <td className="currency">
                    {product.selling_price_per_unit > 0 ? `$${product.selling_price_per_unit.toFixed(2)}` : '-'}
                  </td>
                  <td>{product.supplier || '-'}</td>
                  <td className="actions">
                    <button 
                      className="btn-icon"
                      onClick={() => handleEdit(product)}
                      title="Modifier"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button 
                      className="btn-icon"
                      onClick={() => handleToggleActive(product.id, product.is_active)}
                      title={product.is_active ? 'Désactiver' : 'Activer'}
                    >
                      <Power size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Création/Édition */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingProduct ? 'Modifier le Produit' : 'Nouveau Produit'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nom du Produit *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Ex: Engrais 20-20-20"
                  />
                </div>
                
                <div className="form-group">
                  <label>SKU</label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData({...formData, sku: e.target.value})}
                    placeholder="Ex: ENG-202020"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Catégorie</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    placeholder="Ex: Engrais, Pesticides, Matériel"
                  />
                </div>
                
                <div className="form-group">
                  <label>Fournisseur</label>
                  <input
                    type="text"
                    value={formData.supplier}
                    onChange={(e) => setFormData({...formData, supplier: e.target.value})}
                    placeholder="Ex: Agro Plus Inc."
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Unité *</label>
                  <input
                    type="text"
                    required
                    value={formData.unit}
                    onChange={(e) => setFormData({...formData, unit: e.target.value})}
                    placeholder="Ex: kg, L, sac, unité"
                  />
                </div>
                
                <div className="form-group">
                  <label>Unité d'Application</label>
                  <input
                    type="text"
                    value={formData.default_application_unit}
                    onChange={(e) => setFormData({...formData, default_application_unit: e.target.value})}
                    placeholder="Ex: kg/m²"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Coût par Unité ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.cost_per_unit}
                    onChange={(e) => setFormData({...formData, cost_per_unit: parseFloat(e.target.value)})}
                  />
                </div>
                
                <div className="form-group">
                  <label>Prix de Vente par Unité ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.selling_price_per_unit}
                    onChange={(e) => setFormData({...formData, selling_price_per_unit: parseFloat(e.target.value)})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Notes additionnelles sur le produit..."
                />
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  <Save size={16} />
                  {editingProduct ? 'Mettre à Jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsCatalog;
